import React, { useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import logo from "../assets/ohlins-logo.png";
import icon from "../assets/ohlins-icon.png";
import { AuthContext } from "../context/AuthContext";

function LoginAlt() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { login } = useContext(AuthContext);
  const API_BASE = process.env.REACT_APP_API_BASE || '';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const res = await fetch(`${API_BASE}/api/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });

      if (res.ok) {
        const data = await res.json();
        if (login) {
          login(data.access_token, !!data.isAdmin);
        } else {
          localStorage.setItem("token", data.access_token);
          localStorage.setItem("isAdmin", data.isAdmin);
        }
        navigate("/home");
      } else {
        setError("Invalid email or password");
      }
    } catch (err) {
      setError("Login failed — please try again");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-gray-50 via-gray-100 to-gray-50 relative overflow-hidden">
      {/* Decorative background elements */}
      <div className="absolute top-10 left-10 w-72 h-72 bg-yellow-400/10 rounded-full blur-3xl"></div>
      <div className="absolute bottom-10 right-10 w-96 h-96 bg-black/5 rounded-full blur-3xl"></div>

      <div className="relative z-10 max-w-5xl w-full mx-4 grid grid-cols-1 lg:grid-cols-3 gap-8 items-center">
        {/* Left side - Dark themed sidebar section */}
        <div className="lg:col-span-1 bg-gradient-to-b from-black via-gray-950 to-black rounded-2xl shadow-2xl p-8 border-l-4 border-yellow-400 hidden lg:flex flex-col justify-between" style={{ minHeight: '520px' }}>
          <div>
            <div className="flex items-center justify-center mb-6">
              <img src={icon} alt="Ohlins Icon" className="w-20 h-20 object-contain drop-shadow-lg" />
            </div>
            <img src={logo} alt="Ohlins Logo" className="mx-auto w-40 object-contain drop-shadow-lg mb-6" />
            <div className="h-1 bg-gradient-to-r from-transparent via-yellow-400 to-transparent"></div>
          </div>

          <div className="space-y-4">
            <div className="flex items-center gap-3 text-gray-400">
              <div className="w-2 h-2 bg-yellow-400 rounded-full"></div>
              <span className="text-sm">Travel management made simple</span>
            </div>
            <div className="flex items-center gap-3 text-gray-400">
              <div className="w-2 h-2 bg-yellow-400 rounded-full"></div>
              <span className="text-sm">Stay on track with every trip </span>
            </div>
            <div className="flex items-center gap-3 text-gray-400">
              <div className="w-2 h-2 bg-yellow-400 rounded-full"></div>
              <span className="text-sm">Built for Öhlins</span>
            </div>
          </div>

          <div className="flex items-center justify-center gap-3 pt-6 border-t border-gray-700">
            <svg width="60" height="24" viewBox="0 0 640 200" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M40 150 C80 100, 160 80, 240 100 C320 120, 360 130, 440 120 C520 110, 600 70, 620 50" stroke="#FBBF24" strokeWidth="6" strokeLinecap="round" strokeLinejoin="round" fill="none" opacity="0.7" />
            </svg>
          </div>
        </div>

        {/* Right side - Login card (centered on mobile) */}
        <div className="lg:col-span-2 max-w-md mx-auto w-full">
          {/* Header with branding - mobile only */}
          <div className="text-center mb-8 lg:hidden">
            <div className="flex items-center justify-center mb-4">
              <img src={icon} alt="Ohlins Icon" className="w-20 h-20 object-contain drop-shadow-lg" />
            </div>
            <img src={logo} alt="Ohlins Logo" className="mx-auto h-8 mb-2" />
            <h1 className="text-3xl font-black text-gray-900 tracking-tight">Öhlinstravels</h1>
            <p className="mt-2 text-gray-600 text-sm">Race management made simple</p>
          </div>

          {/* Login card */}
          <div className="bg-white rounded-2xl shadow-2xl p-8 border-t-4 border-yellow-400">
            <h2 className="text-2xl font-bold text-gray-900 mb-1">Sign In</h2>
            <p className="text-gray-500 text-sm mb-6">Enter your credentials to continue</p>

            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm flex items-start gap-3">
                <svg className="w-5 h-5 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                <span>{error}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">Email Address</label>
                <input
                  type="email"
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-yellow-400 focus:outline-none bg-white text-gray-900 placeholder-gray-400 transition"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">Password</label>
                <input
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-yellow-400 focus:outline-none bg-white text-gray-900 placeholder-gray-400 transition"
                  required
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-yellow-400 to-yellow-500 hover:from-yellow-300 hover:to-yellow-400 text-black py-3 rounded-lg font-bold text-lg transition shadow-lg hover:shadow-xl disabled:opacity-60 disabled:cursor-not-allowed mt-8"
              >
                {loading ? 'Signing in…' : 'Sign In'}
              </button>
            </form>

            {/* Footer */}
            <div className="mt-8 pt-6 border-t border-gray-200 lg:hidden">
              <div className="flex items-center justify-center gap-3">
                <svg width="60" height="24" viewBox="0 0 640 200" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M40 150 C80 100, 160 80, 240 100 C320 120, 360 130, 440 120 C520 110, 600 70, 620 50" stroke="#FBBF24" strokeWidth="6" strokeLinecap="round" strokeLinejoin="round" fill="none" opacity="0.7" />
                </svg>
                <span className="text-xs text-gray-500">Pro style · Track ready</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default LoginAlt;
